import * as React from "react";
import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { Loader } from "@/components/common/Loader";
import {
  Mail,
  Phone,
  // Calendar,
  // Clock,
  // ShoppingCart,
  // Receipt,
  // MessageSquare,
  ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Utility } from "@/utility/Utility";
import numeral from "numeral";
import { useCustomers } from "@/contexts/CustomerDataContext";
import { TCustomerModelJSON } from "@shoutout-labs/market_buzz_crm_types";
import {
  getTransactions,
  getTransactionCount
} from "@/services/TransactionService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Constants } from "@/constants";
import FeatureUtils from "@/utility/FeatureUtils";
import { Badge } from "@/components/ui/badge";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { formatCustomerBirthday } from "@/lib/date-utils";
import { usePermissions } from "@/hooks/usePermissions";

dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

export default function CustomerDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { customers, isLoadingCustomers } = useCustomers();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [skip, setSkip] = useState<number>(0);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [totalTransactions, setTotalTransactions] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const customer = customers?.find((c: TCustomerModelJSON) => c.id === id);

  const loadTransactionData = useCallback(async () => {
    if (!customer?.id) return;

    try {
      setLoading(true);
      const queryObj = {
        query: customer.id
      };
      const getTransactionsQueryObj = {
        limit,
        skip,
        customerId: customer.id,
        includeProductItems: false
      };
      const [transactionsRes, transactionCountRes] = await Promise.all([
        getTransactions(getTransactionsQueryObj),
        getTransactionCount(queryObj)
      ]);
      setTransactions(transactionsRes.items);
      setTotalTransactions(transactionCountRes.count);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [customer?.id, limit, skip]);

  useEffect(() => {
    loadTransactionData();
  }, [loadTransactionData]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setSkip((newPage - 1) * limit);
  };

  const totalPages = Math.ceil(totalTransactions / limit);

  const handleSubscribe = () => {
    window.open(Constants.CLOVER_MARKETPLACE_URL, "_blank");
  };

  if (isLoadingCustomers || loading) {
    return <Loader />;
  }

  if (!customer) {
    return (
      <div className="container py-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/customers")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Customers
        </Button>
        <div className="text-center mt-8">Customer not found</div>
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="flex flex-col md:flex-row gap-8">
        {/* Profile Card */}
        <div className="bg-white rounded-lg border p-6 w-full md:w-[340px] flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/customers")}
            className="flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 text-2xl font-bold mb-2">
              {customer.firstName?.[0] || ""}
              {customer.lastName?.[0] || ""}
            </div>
            <div className="text-lg font-semibold">
              {customer.firstName} {customer.lastName}
            </div>
            <div className="flex items-center text-gray-600 mt-1 text-sm">
              <Phone className="h-4 w-4 mr-1" />
              <span>{customer.phoneNumber || "-"}</span>
            </div>
            <div className="flex items-center text-gray-600 mt-1 text-sm">
              <Mail className="h-4 w-4 mr-1" />
              <span>{customer.email || "-"}</span>
            </div>
          </div>
          <div className="border-t pt-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Created On:</span>
              <span>
                {customer?.customerCreatedOn || customer?.createdOn
                  ? dayjs(
                      customer.customerCreatedOn || customer.createdOn
                    ).format("YYYY-MM-DD hh:mm a")
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Last Activity On:</span>
              <span>
                {customer?.lastPurchasedDate
                  ? dayjs(customer?.lastPurchasedDate).format(
                      "YYYY-MM-DD hh:mm a"
                    )
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Date of Birth:</span>
              <span>{formatCustomerBirthday(customer)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Marketing Permission:</span>
              <Badge
                variant={
                  customer.optIn?.marketing?.allowed ? "default" : "destructive"
                }
              >
                {customer.optIn?.marketing?.allowed ? "Enable" : "Disable"}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Visits:</span>
              <span>{customer?.totalTransactionsCount || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Avg. Spend:</span>
              <span>
                $
                {numeral(
                  Utility.getAvgSpend(
                    customer.totalTransactionsCount || 1,
                    customer.totalTransactionsSum || 0
                  )
                ).format("0.00")}
              </span>
            </div>
          </div>
          {customer?.metadata?.customAttributes &&
            Object.keys(customer.metadata.customAttributes).length > 0 && (
              <div className="border-t pt-4 space-y-3 text-sm">
                <div className="text-gray-500 mb-2">Custom Attributes:</div>
                {Object.entries(customer.metadata.customAttributes).map(
                  ([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-500 capitalize">{key}:</span>
                      <span>{value as string}</span>
                    </div>
                  )
                )}
              </div>
            )}
        </div>

        {/* Transactions Table */}
        <div className="flex-1 overflow-x-auto">
          <h3 className="text-lg font-semibold mb-4">Transactions</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Transaction ID</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length > 0 ? (
                (!FeatureUtils.isCRMEnabled(permissions)
                  ? [transactions[0]]
                  : transactions
                ).map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell className="font-medium">
                      {transaction.id}
                    </TableCell>
                    <TableCell>
                      ${numeral(transaction.amount).format("0.00")}
                    </TableCell>
                    <TableCell>
                      {transaction.transactionOnLocalTime
                        ? dayjs
                            .utc(transaction.transactionOnLocalTime)
                            .format("MMMM Do YYYY, h:mm:ss a [UTC]")
                        : dayjs(transaction.transactionOn).format("LL LTS")}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-gray-500">
                    No transactions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {!FeatureUtils.isCRMEnabled(permissions) &&
            transactions.length > 0 && (
              <div className="mt-4 text-center border rounded-md p-6">
                <h3 className="text-lg font-semibold mb-2">
                  Unlock the full customer story—spending habits, visits,
                  contacts, and more!
                </h3>
                <p className="mb-4">Subscribe now!</p>
                <Button
                  onClick={handleSubscribe}
                  className="bg-[#128f8b] hover:bg-[#0f7a76] text-white"
                >
                  Subscribe
                </Button>
              </div>
            )}

          {/* Pagination */}
          {transactions.length > 0 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {transactions.length} of {totalTransactions} results
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      return (
                        page === 1 ||
                        page === totalPages ||
                        Math.abs(currentPage - page) <= 1
                      );
                    })
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <Button
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          className="min-w-[32px]"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Button>
                      </React.Fragment>
                    ))}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
